import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { adminContractsAPI } from '../../services/api';
import { formatMoney } from '../../utils/format';
import RateCardEditor from './RateCardEditor';

/**
 * Billing in ConnectWise, for one signed contract: the agreement this
 * document becomes — a new one, or lines added to the client's existing
 * agreement of the type — reviewed here and created with one button.
 *
 * A signed quote has usually done this already (its lines carry products and
 * prices); this card then shows what was created. An MSA, when MSAs are
 * parents, shows its parent agreement and offers to put the client's other
 * agreements under it.
 */
const card = 'rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 p-6 space-y-4';
const errorOf = (e, fallback) => e?.response?.data?.error || e?.message || fallback;
const STATUS = {
  draft: ['Needs review', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  ready: ['Ready to create', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'],
  creating: ['Creating…', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'],
  created: ['Created in ConnectWise', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'],
  failed: ['ConnectWise refused it', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'],
  skipped: ['Skipped', 'bg-neutral-100 text-neutral-600'],
};

function CatalogPicker({ onPick }) {
  const [q, setQ] = useState('');
  const { data } = useQuery({
    queryKey: ['contract-catalog', q],
    queryFn: () => adminContractsAPI.catalog(q).then((r) => r.data.items),
    enabled: q.length >= 2,
  });
  return (
    <div className="relative">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a product: search the catalog" className="input w-72" />
      {q.length >= 2 && data?.length > 0 && (
        <ul className="absolute z-10 mt-1 w-96 max-h-60 overflow-auto rounded-lg border border-neutral-200 bg-white shadow dark:border-neutral-700 dark:bg-neutral-800">
          {data.map((i) => (
            <li key={i.id}>
              <button onClick={() => { onPick(i); setQ(''); }} className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 dark:text-neutral-200">
                <span className="font-medium">{i.identifier}</span> · {i.description} {i.price != null ? `· ${formatMoney(i.price)}` : ''}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ContractAgreementCard({ contract }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [adopt, setAdopt] = useState([]);
  const key = ['contract-agreement', contract.id];
  const { data, isLoading, refetch } = useQuery({ queryKey: key, queryFn: () => adminContractsAPI.agreement(contract.id).then((r) => r.data) });
  const settings = useQuery({ queryKey: ['contract-settings'], queryFn: () => adminContractsAPI.settings().then((r) => r.data) });

  const plan = data?.plan;
  useEffect(() => {
    if (!plan) return;
    setDraft({
      mode: plan.mode, targetAgreementId: plan.target_agreement_id || '', agreementTypeId: plan.agreement_type_id || '',
      billingCycleId: plan.billing_cycle_id || '', contactId: plan.contact_id || '', startDate: plan.start_date ? String(plan.start_date).slice(0, 10) : '',
      agreementName: plan.agreement_name || '', lines: plan.lines || [],
      coverage: {
        coverTime: plan.cover_time === true, coverProducts: plan.cover_products === true, coverExpenses: plan.cover_expenses === true,
        rates: plan.work_type_rates || [],
      },
    });
  }, [plan]);

  const run = async (name, fn) => {
    setBusy(name);
    setError('');
    try {
      const r = await fn();
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['admin-contract', contract.id] });
      return r;
    } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      await refetch();
      return null;
    } finally {
      setBusy('');
    }
  };

  if (isLoading) return <div className={card}><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>;

  const editable = plan && !['creating', 'created'].includes(plan.status) && !plan.created_agreement_id && !Object.keys(plan.created_additions || {}).length;
  const setLine = (i, patch) => setDraft({ ...draft, lines: draft.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const { coverage, ...rest } = draft || {};
  const save = () => run('save', () => adminContractsAPI.updateAgreement(contract.id, {
    ...rest,
    ...(rest.mode === 'new' && coverage ? {
      coverTime: coverage.coverTime, coverProducts: coverage.coverProducts, coverExpenses: coverage.coverExpenses,
      workTypeRates: coverage.rates.map((r) => ({ ...r, rate: Number(r.rate) || 0, hoursMin: r.hoursMin === null || r.hoursMin === '' ? null : Number(r.hoursMin), hoursMax: r.hoursMax === null || r.hoursMax === '' ? null : Number(r.hoursMax) })),
    } : {}),
    targetAgreementId: draft.mode === 'extend' ? draft.targetAgreementId : null,
    lines: draft.lines.map((l) => ({ ...l, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice), unitCost: Number(l.unitCost || 0) })),
  }));
  const total = (draft?.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0);
  const types = settings.data?.types || [];
  const cycles = settings.data?.cycles || [];
  const label = 'block text-sm text-neutral-600 dark:text-neutral-300 mb-1';

  return (
    <div className={card}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold dark:text-white">Billing in ConnectWise</h2>
        {plan && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS[plan.status]?.[1]}`}>{STATUS[plan.status]?.[0]}</span>}
      </div>
      {error && <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</div>}

      {!plan && (
        <div className="space-y-3">
          <p className="text-sm text-neutral-600 dark:text-neutral-300">No billing agreement has been planned for this document.</p>
          {['order', 'sow', 'quote', 'msa'].includes(contract.kind) && (
            <button disabled={!!busy} onClick={() => run('plan', () => adminContractsAPI.planAgreement(contract.id))} className="btn-secondary">Plan the agreement</button>
          )}
        </div>
      )}

      {plan && (plan.review_reasons || []).length > 0 && plan.status === 'draft' && (
        <ul className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200 list-disc pl-6">
          {plan.review_reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      {plan?.status === 'failed' && plan.error && <p className="text-sm text-red-700 dark:text-red-300">ConnectWise said: {plan.error}</p>}

      {plan?.status === 'created' && (
        <div className="space-y-2 text-sm dark:text-neutral-200">
          <p className="flex items-center gap-2 text-green-700 dark:text-green-400"><CheckCircle className="w-4 h-4" />
            {plan.mode === 'extend' ? `${(plan.lines || []).length} line(s) added to agreement #${plan.created_agreement_id || plan.target_agreement_id}` : `Agreement #${plan.created_agreement_id} created`}
            {plan.mode === 'msa_parent' ? ' (MSA parent; bills nothing itself)' : ''}, starting {String(plan.start_date).slice(0, 10)}.
          </p>
          {plan.mode !== 'msa_parent' && <p className="text-neutral-500">{(plan.lines || []).map((l) => `${l.quantity} × ${l.description} @ ${formatMoney(l.unitPrice)}`).join(' · ')}</p>}
        </div>
      )}

      {plan?.mode === 'msa_parent' && plan.status === 'created' && (data.agreements || []).some((a) => a.id !== plan.created_agreement_id && !a.parentId) && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 space-y-2">
          <p className="text-sm dark:text-neutral-200">Put the client&apos;s existing agreements under this MSA:</p>
          {(data.agreements || []).filter((a) => a.id !== plan.created_agreement_id && !a.parentId).map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm dark:text-neutral-200">
              <input type="checkbox" checked={adopt.includes(a.id)} onChange={(e) => setAdopt(e.target.checked ? [...adopt, a.id] : adopt.filter((x) => x !== a.id))} />
              {a.name} <span className="text-neutral-500">({a.type})</span>
            </label>
          ))}
          <button disabled={!adopt.length || !!busy} onClick={async () => { await run('adopt', () => adminContractsAPI.adoptUnderMsa(contract.id, adopt)); setAdopt([]); }} className="btn-secondary">Move under the MSA</button>
        </div>
      )}

      {plan && plan.mode !== 'msa_parent' && plan.status !== 'created' && draft && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <span className={label}>Agreement</span>
              <select value={draft.mode === 'extend' ? String(draft.targetAgreementId) : 'new'} disabled={!editable}
                onChange={(e) => setDraft({ ...draft, mode: e.target.value === 'new' ? 'new' : 'extend', targetAgreementId: e.target.value === 'new' ? '' : Number(e.target.value) })} className="input w-full">
                <option value="new">A new agreement</option>
                {(data.agreements || []).map((a) => <option key={a.id} value={a.id}>Add to: {a.name} ({a.type})</option>)}
              </select>
            </label>
            <label>
              <span className={label}>Lines start (and bill from)</span>
              <input type="date" value={draft.startDate} disabled={!editable} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} className="input w-full" />
            </label>
            {draft.mode === 'new' && (
              <>
                <label>
                  <span className={label}>Agreement type</span>
                  <select value={draft.agreementTypeId} disabled={!editable} className="input w-full"
                    onChange={(e) => {
                      // A different type brings its own rate card, as the server would.
                      const c = (settings.data?.settings?.rateCards || {})[e.target.value] || {};
                      setDraft({ ...draft, agreementTypeId: e.target.value, coverage: { coverTime: c.coverTime === true, coverProducts: c.coverProducts === true, coverExpenses: c.coverExpenses === true, rates: c.rates || [] } });
                    }}>
                    <option value="">Choose…</option>
                    {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
                <label>
                  <span className={label}>Billing cycle</span>
                  <select value={draft.billingCycleId} disabled={!editable} onChange={(e) => setDraft({ ...draft, billingCycleId: e.target.value })} className="input w-full">
                    <option value="">The type&apos;s default</option>
                    {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label className="md:col-span-2">
                  <span className={label}>Agreement name</span>
                  <input value={draft.agreementName} disabled={!editable} onChange={(e) => setDraft({ ...draft, agreementName: e.target.value })} maxLength={100} className="input w-full" />
                </label>
                {!draft.contactId && <p className="md:col-span-2 text-sm text-amber-700">No ConnectWise contact was found for this client; the agreement needs one. Add a signer picked from ConnectWise contacts, or set the company&apos;s default contact in ConnectWise.</p>}
                <div className="md:col-span-2 space-y-2">
                  <span className={label}>Coverage and rates for this agreement</span>
                  <RateCardEditor value={draft.coverage} disabled={!editable} onChange={(c) => setDraft({ ...draft, coverage: c })} />
                </div>
              </>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-neutral-500 dark:text-neutral-400">
                <tr><th className="py-2 pr-3 font-medium">Product</th><th className="py-2 pr-3 font-medium w-24">Qty</th><th className="py-2 pr-3 font-medium w-32">Price / cycle</th><th className="py-2 pr-3 font-medium w-32">Cost</th><th /></tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {draft.lines.map((l, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-3 dark:text-neutral-200">
                      <div className="font-medium">{l.identifier || `Product #${l.catalogItemId}`}</div>
                      <input value={l.description} disabled={!editable} onChange={(e) => setLine(i, { description: e.target.value })} className="input w-full mt-1 text-xs" />
                    </td>
                    <td className="py-2 pr-3"><input type="number" min="0" step="any" value={l.quantity} disabled={!editable} onChange={(e) => setLine(i, { quantity: e.target.value })} className="input w-full" /></td>
                    <td className="py-2 pr-3"><input type="number" min="0" step="0.01" value={l.unitPrice} disabled={!editable} onChange={(e) => setLine(i, { unitPrice: e.target.value })} className="input w-full" /></td>
                    <td className="py-2 pr-3"><input type="number" min="0" step="0.01" value={l.unitCost ?? 0} disabled={!editable} onChange={(e) => setLine(i, { unitCost: e.target.value })} className="input w-full" /></td>
                    <td className="py-2">{editable && <button onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })} className="p-1 text-neutral-400 hover:text-red-600" aria-label="Remove line"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
                {!draft.lines.length && <tr><td colSpan={5} className="py-4 text-neutral-500">No lines yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">Each billing cycle: {formatMoney(total)}</p>
          {(plan.not_billed || []).length > 0 && (
            <p className="text-xs text-neutral-500">Not on this agreement (one-time, invoiced separately): {plan.not_billed.map((n) => `${n.quantity} × ${n.description}`).join(', ')}</p>
          )}

          {editable && (
            <div className="flex flex-wrap items-center gap-3">
              <CatalogPicker onPick={(i) => setDraft({ ...draft, lines: [...draft.lines, { catalogItemId: i.id, identifier: i.identifier, description: i.description || i.identifier, quantity: 1, unitPrice: i.price ?? 0, unitCost: i.cost ?? 0, billCustomer: 'Billable' }] })} />
              <button disabled={!!busy} onClick={() => run('load', () => adminContractsAPI.loadAgreementLines(contract.id))} className="btn-secondary inline-flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Load from the opportunity</button>
              <button disabled={!!busy} onClick={save} className="btn-secondary inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Save changes</button>
            </div>
          )}
          <button
            disabled={!!busy || !draft.lines.length}
            onClick={async () => { if (editable) await save(); await run('create', () => adminContractsAPI.createAgreement(contract.id)); }}
            className="btn-primary inline-flex items-center gap-2"
          >
            {busy === 'create' && <Loader2 className="w-4 h-4 animate-spin" />}
            {plan.status === 'failed' ? 'Try again in ConnectWise' : draft.mode === 'extend' ? 'Add these lines in ConnectWise' : 'Create the agreement in ConnectWise'}
          </button>
        </div>
      )}

      {plan?.mode === 'msa_parent' && plan.status !== 'created' && (
        <button disabled={!!busy} onClick={() => run('create', () => adminContractsAPI.createAgreement(contract.id))} className="btn-primary">
          {plan.status === 'failed' ? 'Try again' : 'Create the MSA parent agreement'}
        </button>
      )}
    </div>
  );
}
